import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { completeRegistration } from '../../api/authApi';

const CompleteRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, updateUserProfile } = useAuth();
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    phone_number: Yup.string()
      .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit phone number')
      .required('Phone number is required'),
    hostel_name: Yup.string()
      .required('Hostel name is required')
      .max(200, 'Hostel name is too long'),
    room_number: Yup.string()
      .required('Room number is required')
      .max(20, 'Room number is too long')
  });

  const formik = useFormik({
    initialValues: {
      phone_number: '',
      hostel_name: '',
      room_number: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError('');
        
        const response = await completeRegistration(values);
        
        if (response && response.user) {
          updateUserProfile(response.user);
          toast.success('Registration completed successfully!');
          navigate('/dashboard');
        } else {
          setError('Failed to complete registration. Please try again.');
        }
      } catch (error) {
        console.error('Registration error:', error);
        setError(error.message || 'An error occurred during registration.');
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <Container className="py-5">
      <Card className="shadow-sm">
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <h2>Complete Your Registration</h2>
            <p className="text-muted">Please provide the following details to complete your registration</p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your 10-digit phone number"
                name="phone_number"
                value={formik.values.phone_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.phone_number && formik.errors.phone_number}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.phone_number}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Hostel Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your hostel name"
                name="hostel_name"
                value={formik.values.hostel_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.hostel_name && formik.errors.hostel_name}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.hostel_name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Room Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your room number"
                name="room_number"
                value={formik.values.room_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.room_number && formik.errors.room_number}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.room_number}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading || !formik.isValid}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : 'Complete Registration'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CompleteRegistration;